import styled from "styled-components";
import { useEffect, useState } from "react";
import { startOfDay } from "date-fns";
import { useAdmin } from "../../../context/admin";
import { useGlobal } from "../../../context/global";
import AdminService from "../../../services/admin.service";
import { CashAccount, CashCategory, CashFlowType, CashMovement } from "../../../types/common.types";
import Filter from "./Filter";
import List from "./List";
import PendingPayments from "./PendingPayments";
import TripCostDebts from "./TripCostDebts";
import AdminRegisterCheckpoint from "./AdminRegisterCheckpoint";
import DateRangeFilter from "./DateRangeFilter";
import Balances from "./Balances";
import BankReconciliation from "./BankReconciliation";

export default function Caja() {
    const { setIsCreateCashMovementModalOpen } = useAdmin()
    const { setIsLoading } = useGlobal()
    const [view, setView] = useState<'movements' | 'pending' | 'debts' | 'checkpoint' | 'balances' | 'reconciliation'>('movements')
    const [accounts, setAccounts] = useState<CashAccount[]>([])
    const [categories, setCategories] = useState<CashCategory[]>([])
    const [movements, setMovements] = useState<CashMovement[]>([])
    const [accountId, setAccountId] = useState('')
    const [movementType, setMovementType] = useState<CashFlowType | ''>('')
    const [categoryId, setCategoryId] = useState('')
    // Medianoche de hoy, no el instante exacto: si se manda new Date() tal cual, en la noche (Chile va
    // detrás de UTC) el día calendario ya rodó al siguiente en UTC, y el servidor arma el filtro
    // pensando que "hoy" es mañana — dejando fuera todos los movimientos de hoy.
    const [currentDay, setCurrentDay] = useState<Date | undefined>(startOfDay(new Date()))
    const [endDay, setEndDay] = useState<Date | undefined>()
    const [unassignedVentas, setUnassignedVentas] = useState(false)

    useEffect(() => {
        async function loadCatalogs() {
            try {
                const [accountsRes, categoriesRes] = await Promise.all([
                    AdminService.listCashAccounts(),
                    AdminService.listCashCategories()
                ])
                setAccounts(accountsRes.data)
                setCategories(categoriesRes.data)
            } catch (error) {
                console.log(error)
            }
        }
        loadCatalogs()
    }, [])

    async function loadMovements() {
        setIsLoading(true)
        try {
            const response = await AdminService.listCashMovements({
                from: currentDay,
                to: endDay,
                account_id: accountId || undefined,
                movement_type: movementType || undefined,
                category_id: categoryId || undefined,
                unassigned_ventas: unassignedVentas || undefined,
            })
            setMovements(response.data)
        } catch (error) {
            console.log(error)
        } finally {
            setIsLoading(false)
        }
    }

    useEffect(() => {
        if (view === 'movements') loadMovements()
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [currentDay, endDay, accountId, movementType, categoryId, unassignedVentas, setIsLoading, view])

    return (
        <Main>
            <DateRangeFilter day={currentDay} endDay={endDay} setDay={setCurrentDay} setEndDay={setEndDay} />
            <div className="toggle">
                <button type="button" className={view === 'movements' ? 'active' : ''} onClick={() => setView('movements')}>Movimientos</button>
                <button type="button" className={view === 'pending' ? 'active' : ''} onClick={() => setView('pending')}>Pagos pendientes</button>
                <button type="button" className={view === 'debts' ? 'active' : ''} onClick={() => setView('debts')}>Deudas de trip</button>
                <button type="button" className={view === 'checkpoint' ? 'active' : ''} onClick={() => setView('checkpoint')}>Ajuste de caja</button>
                <button type="button" className={view === 'balances' ? 'active' : ''} onClick={() => setView('balances')}>Saldos</button>
                <button type="button" className={view === 'reconciliation' ? 'active' : ''} onClick={() => setView('reconciliation')}>Conciliación bancaria</button>
            </div>
            {view === 'movements' && (
                <>
                    <Filter
                        accounts={accounts}
                        categories={categories}
                        accountId={accountId}
                        setAccountId={setAccountId}
                        movementType={movementType}
                        setMovementType={setMovementType}
                        categoryId={categoryId}
                        setCategoryId={setCategoryId}
                    />
                    <div className="actions">
                        <button className="add-new" onClick={() => setIsCreateCashMovementModalOpen(true)}>
                            <span>
                                <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" width="24" height="24"><path fill="none" d="M0 0h24v24H0z"></path><path fill="currentColor" d="M11 11V5h2v6h6v2h-6v6h-2v-6H5v-2z"></path></svg>
                                Nuevo movimiento
                            </span>
                        </button>
                        <label className="unassigned-toggle">
                            <input
                                type="checkbox"
                                checked={unassignedVentas}
                                onChange={(e) => setUnassignedVentas(e.target.checked)}
                            />
                            Solo Ventas sin asignar
                        </label>
                    </div>
                    <List list={movements} onChanged={loadMovements} />
                </>
            )}
            {view === 'pending' && <PendingPayments day={currentDay} endDay={endDay} />}
            {view === 'debts' && <TripCostDebts day={currentDay} endDay={endDay} />}
            {view === 'checkpoint' && <AdminRegisterCheckpoint />}
            {view === 'balances' && <Balances day={currentDay} endDay={endDay} />}
            {view === 'reconciliation' && <BankReconciliation />}
        </Main>
    )
}

const Main = styled.div`
.toggle {
    display: flex;
    gap: 10px;
    margin: 15px 0;
    flex-wrap: wrap;
    button {
        padding: 8px 16px;
        border-radius: 8px;
        border: 2px solid ${props => props.theme.blue};
        background: transparent;
        color: ${props => props.theme.blue};
        cursor: pointer;
        font-weight: 600;
    }
    button.active {
        background-color: ${props => props.theme.blue};
        color: ${props => props.theme.white};
    }
}
.actions {
    display: flex;
    align-items: center;
    gap: 15px;
    flex-wrap: wrap;
    margin: 10px 0 20px;
}
.unassigned-toggle {
    display: flex;
    align-items: center;
    gap: 6px;
    font-size: 14px;
    font-weight: 600;
    cursor: pointer;
    color: ${props => props.theme.gray};
    input {
        cursor: pointer;
    }
}
.add-new {
    border: 2px solid ${props => props.theme.yellowDark};
    background-color: ${props => props.theme.yellowDark};
    border-radius: 0.9em;
    padding: 10px 20px 10px 12px;
    cursor: pointer;
    span {
        display: flex;
        justify-content: center;
        align-items: center;
        gap: 5px;
        color: ${props => props.theme.white};
        font-weight: 600;
    }
    &:active {
        transform: scale(0.95);
    }
}
`
